/**
 * Chess Roster Reveal — NBA 2K-style coach cards for all 6 chess pieces.
 * Each card flips: front = OVR + stats + comps, back = movement diagram +
 * special capabilities + signature moves.
 */
import React, { useState } from 'react';
import { Crown, Zap, Target, Flame, ShieldAlert, Users, RotateCcw, Sparkles, Move, Award } from 'lucide-react';

// ---------- ROSTER DATA ----------
const PIECES = [
  {
    id: 'queen', code: 'PG1', glyph: '♛', accent: '#C9A961',
    name: 'The Queen', role: 'Floor General', position: 'POINT GUARD · CHESS',
    ovr: 99, value: 9,
    stats: [
      { key: 'RNG', label: 'Range',         value: 99, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 96, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 95, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 94, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 99, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 42, color: '#71717A' },
    ],
    comps: ['Chris Paul', 'Magic Johnson', 'LeBron (PG mode)', 'Steph Curry off-ball'],
    startPos: [3, 3],
    movement: { type: 'rays', vectors: [[1,0],[-1,0],[0,1],[0,-1],[1,1],[-1,-1],[1,-1],[-1,1]], unlimited: true },
    movePattern: 'Straight lines (like a Rook) + Diagonals (like a Bishop). Any direction, any distance, until something blocks her.',
    capabilities: ['8-direction movement', 'Unlimited range', 'Highest attack coverage (up to 27 squares)'],
    coachNote: "Don't bring her out before Move 6-7. Every early-Queen attack (like the Scholar's Mate in Session 4) gets punished. She's the closer — save her for when the roster is set.",
    signatureMoves: [
      { icon: Target, name: 'The Fork', desc: 'Attacks two enemy pieces at once — pick your poison' },
      { icon: Flame,  name: 'Back-Rank Mate', desc: 'Rides the 8th rank into a game-ender' },
      { icon: Zap,    name: 'Skewer', desc: 'Aligns two enemies on one line — forces a trade-down' },
      { icon: Users,  name: 'Discovered Check', desc: 'Moves off a diagonal to unmask a bishop or rook attack' },
    ],
  },
  {
    id: 'rook', code: 'BIG5', glyph: '♜', accent: '#EF4444',
    name: 'The Rook', role: 'Twin Tower · Rim Protector', position: 'CENTER · CHESS',
    ovr: 87, value: 5,
    stats: [
      { key: 'RNG', label: 'Range',         value: 92, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 62, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 80, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 91, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 84, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 34, color: '#71717A' },
    ],
    comps: ['Shaquille O\u2019Neal', 'Tim Duncan', 'Nikola Joki\u0107', 'Bill Russell'],
    startPos: [3, 3],
    movement: { type: 'rays', vectors: [[1,0],[-1,0],[0,1],[0,-1]], unlimited: true },
    movePattern: 'Straight lines only — up, down, left, right. Any number of squares until something blocks. The owner of open files and open ranks.',
    capabilities: ['4-direction movement (files + ranks)', 'Unlimited range in a straight line', 'Castles with the King (special move)', 'Deadliest on open boards & endgames'],
    coachNote: "Bring your Rooks out LAST. They shine on open files and in the endgame — like a big man dominating a wide-open floor after everyone else has cleared out.",
    signatureMoves: [
      { icon: Target, name: 'Rook Lift',       desc: 'Slide the Rook up a rank to attack the enemy king from the side' },
      { icon: Flame,  name: 'Seventh Rank',    desc: 'Rook on the enemy\u2019s 2nd row = camping in the paint' },
      { icon: Zap,    name: 'Doubled Rooks',   desc: 'Two Rooks on the same open file = a double-team down low' },
      { icon: Users,  name: 'Castling Partner',desc: 'Swaps with the King in one move for franchise safety' },
    ],
  },
  {
    id: 'bishop', code: 'SG3', glyph: '♝', accent: '#22D3EE',
    name: 'The Bishop', role: 'Wing Sniper · 3-Point Shooter', position: 'SHOOTING GUARD · CHESS',
    ovr: 82, value: 3,
    stats: [
      { key: 'RNG', label: 'Range',         value: 90, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 70, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 66, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 75, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 58, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 78, color: '#71717A' },
    ],
    comps: ['Ray Allen', 'Klay Thompson', 'Reggie Miller', 'Steph Curry catch-and-shoot'],
    startPos: [3, 3],
    movement: { type: 'rays', vectors: [[1,1],[-1,-1],[1,-1],[-1,1]], unlimited: true },
    movePattern: 'Diagonals only — any distance until something blocks. Stays on ONE color square forever. Two Bishops together cover both colors like an elite shooting duo.',
    capabilities: ['4-direction diagonal movement', 'Unlimited range on diagonals', 'Color-locked (light-square OR dark-square, never both)', 'Bishop Pair bonus: two Bishops working together is worth more than the sum'],
    coachNote: 'A Bishop needs an OPEN diagonal — like a shooter needs a clean look. Pawn structure that opens diagonals = Bishop paradise. Closed pawn chains = Bishop in mud.',
    signatureMoves: [
      { icon: Target,   name: 'The Pin',        desc: 'Freezes an enemy piece against something more valuable' },
      { icon: Flame,    name: 'Fianchetto',     desc: 'Bishop tucks behind a pawn wall on b2/g2 for max diagonal range' },
      { icon: Sparkles, name: 'Long Diagonal',  desc: 'Sniper across the board — a1-to-h8 clean look' },
      { icon: Users,    name: 'Bishop Pair',    desc: 'Two shooters on both colors = defense can\u2019t rotate' },
    ],
  },
  {
    id: 'knight', code: 'SF6', glyph: '♞', accent: '#10B981',
    name: 'The Knight', role: 'Slashing Sixth Man · Athletic Wing', position: 'SIXTH MAN · CHESS',
    ovr: 85, value: 3,
    stats: [
      { key: 'RNG', label: 'Range',         value: 55, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 96, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 72, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 82, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 60, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 92, color: '#71717A' },
    ],
    comps: ['Manu Ginóbili', 'Jimmy Butler', 'Kawhi Leonard', 'Dwyane Wade'],
    startPos: [3, 3],
    movement: { type: 'jumps', vectors: [[1,2],[2,1],[-1,2],[-2,1],[1,-2],[2,-1],[-1,-2],[-2,-1]] },
    movePattern: 'L-shape jumps: 2 squares one direction + 1 square perpendicular. THE ONLY PIECE THAT JUMPS OVER OTHERS — bulldozes through packed defenses.',
    capabilities: ['8 fixed L-shaped landing squares', 'JUMPS over any piece (only piece with this ability)', 'Devastating in closed positions', 'Best forker on the board — attacks 2 pieces at once'],
    coachNote: 'The Knight is your GO-TO piece when the board is crowded — like a slasher who thrives when the paint is packed. Develop Knights EARLY (before Bishops) so they can leap into the fight.',
    signatureMoves: [
      { icon: Target,   name: 'The Fork',           desc: 'Attacks two enemies at once from a single L-jump' },
      { icon: Flame,    name: 'Outpost Knight',     desc: 'Knight anchored on a strong square deep in enemy zone' },
      { icon: Zap,      name: 'Smothered Mate',     desc: 'Only the Knight can mate a King trapped by his own pieces' },
      { icon: Sparkles, name: 'The Windmill',       desc: 'Knight + Rook combos that never stop cycling' },
    ],
  },
  {
    id: 'king', code: 'FR∞', glyph: '♚', accent: '#A78BFA',
    name: 'The King', role: 'The Franchise · Priceless', position: 'FRANCHISE PLAYER · CHESS',
    ovr: 100, value: '∞',
    stats: [
      { key: 'RNG', label: 'Range',         value: 20, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 25, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 40, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 100, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 100, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 22, color: '#71717A' },
    ],
    comps: ['Kobe Bryant', 'Michael Jordan (as a franchise)', 'Bill Russell in playoffs', 'LeBron as a franchise cornerstone'],
    startPos: [3, 3],
    movement: { type: 'rays', vectors: [[1,0],[-1,0],[0,1],[0,-1],[1,1],[-1,-1],[1,-1],[-1,1]], unlimited: false, maxSteps: 1 },
    movePattern: 'One square in any direction. Slowest player on the roster — but lose him and the game is over.',
    capabilities: ['8-direction movement, 1 square per turn', 'CANNOT move into check (the rules protect him)', 'CASTLES with a Rook — special 2-for-1 defensive move', 'Activates in the endgame — becomes a real attacker once the danger is gone'],
    coachNote: 'Middlegame: HIDE him. Castle by move 8-9. Endgame: ACTIVATE him. In the endgame with few pieces left, the King becomes a strong piece who marches to the center to help finish games.',
    signatureMoves: [
      { icon: ShieldAlert, name: 'Castling',         desc: 'Swap with a Rook in one move — protects the Franchise' },
      { icon: Award,       name: 'King Safety',      desc: 'Pawn shield in front + Rook behind = fortress' },
      { icon: Flame,       name: 'Endgame Activation', desc: 'March the King to the center once the board thins out' },
      { icon: Target,      name: 'The Opposition',   desc: 'Endgame kings duel head-to-head — a spacing battle' },
    ],
  },
  {
    id: 'pawn', code: 'RP1', glyph: '♟', accent: '#71717A',
    name: 'The Pawn', role: 'Role Player · Grinder', position: 'ROLE PLAYER · CHESS',
    ovr: 68, value: 1,
    stats: [
      { key: 'RNG', label: 'Range',         value: 15, color: '#F97316' },
      { key: 'MOB', label: 'Mobility',      value: 20, color: '#C9A961' },
      { key: 'CTR', label: 'Court Control', value: 55, color: '#22D3EE' },
      { key: 'CLU', label: 'Clutch',        value: 62, color: '#10B981' },
      { key: 'PWR', label: 'Power',         value: 20, color: '#EF4444' },
      { key: 'ERA', label: 'Early-Game IQ', value: 88, color: '#71717A' },
    ],
    comps: ['Draymond Green (glue guy)', 'Tony Allen', 'Bruce Bowen', 'Any deep-bench grinder'],
    startPos: [3, 5],
    movement: { type: 'pawn' }, // custom rendering
    movePattern: 'Moves 1 square forward. Captures 1 square diagonally forward. On its FIRST MOVE, can sprint 2 squares. Cannot move backward.',
    capabilities: ['1 square forward per turn', 'DIAGONAL captures only (never straight)', 'FIRST-MOVE SPRINT: 2 squares (once per pawn)', 'EN PASSANT: capture a pawn that sprints past your capture zone (only on the very next turn)', 'PROMOTION: reach the far end of the board = turns into a Queen (or any piece)'],
    coachNote: "Pawns are the SKELETON of every position. Their structure determines where every other piece can go. Weak pawns = weak team. Strong pawn chain = fortress. And every pawn is one great push away from becoming a PG1.",
    signatureMoves: [
      { icon: Award,    name: 'Promotion',      desc: 'Reach the 8th rank → transform into a Queen (biggest comeback mechanic)' },
      { icon: Zap,      name: 'En Passant',     desc: 'Steal an enemy pawn that sprints past you — one-turn window' },
      { icon: Sparkles, name: 'Passed Pawn',    desc: 'A pawn with no enemies in front on adjacent files — a fast break in slow motion' },
      { icon: Users,    name: 'Pawn Chain',     desc: 'Linked pawns defending each other diagonally — a locked pick-and-roll wall' },
    ],
  },
];

// ---------- MOVE DIAGRAM ----------
const SIZE = 8;

const computeAttackedSquares = (piece) => {
  const [qx, qy] = piece.startPos;
  const attacked = new Set();
  const mv = piece.movement;
  if (mv.type === 'rays') {
    const maxSteps = mv.maxSteps || SIZE;
    mv.vectors.forEach(([dx, dy]) => {
      for (let step = 1; step <= maxSteps; step++) {
        const nx = qx + dx * step;
        const ny = qy + dy * step;
        if (nx < 0 || nx >= SIZE || ny < 0 || ny >= SIZE) break;
        attacked.add(`${nx},${ny}`);
        if (!mv.unlimited && step >= (mv.maxSteps || 1)) break;
      }
    });
  } else if (mv.type === 'jumps') {
    mv.vectors.forEach(([dx, dy]) => {
      const nx = qx + dx;
      const ny = qy + dy;
      if (nx >= 0 && nx < SIZE && ny >= 0 && ny < SIZE) attacked.add(`${nx},${ny}`);
    });
  }
  // pawn handled separately in renderer
  return attacked;
};

const MoveDiagram = ({ piece }) => {
  const [qx, qy] = piece.startPos;
  const attacked = piece.movement.type === 'pawn' ? new Set() : computeAttackedSquares(piece);
  // Pawn: forward moves & diagonal captures (white pawn moves toward y=0)
  const pawnForward = new Set();
  const pawnCapture = new Set();
  if (piece.movement.type === 'pawn') {
    // Forward one and sprint two (up on board = smaller y)
    if (qy - 1 >= 0) pawnForward.add(`${qx},${qy - 1}`);
    if (qy - 2 >= 0) pawnForward.add(`${qx},${qy - 2}`); // sprint marker
    // Diagonal captures
    if (qx - 1 >= 0 && qy - 1 >= 0) pawnCapture.add(`${qx - 1},${qy - 1}`);
    if (qx + 1 < SIZE && qy - 1 >= 0) pawnCapture.add(`${qx + 1},${qy - 1}`);
  }

  return (
    <div className="w-full max-w-[200px]">
      <div className="grid grid-cols-8 gap-0 w-full border-2" style={{ borderColor: `${piece.accent}80` }}>
        {Array.from({ length: SIZE * SIZE }).map((_, i) => {
          const x = i % SIZE;
          const y = Math.floor(i / SIZE);
          const isLight = (x + y) % 2 === 0;
          const isPiece = x === qx && y === qy;
          const isAttacked = attacked.has(`${x},${y}`);
          const isPawnMove = pawnForward.has(`${x},${y}`);
          const isPawnCap  = pawnCapture.has(`${x},${y}`);
          let bg;
          if (isPiece) bg = piece.accent;
          else if (isAttacked || isPawnMove) bg = `${piece.accent}44`;
          else if (isPawnCap) bg = 'rgba(239,68,68,0.35)';
          else bg = isLight ? '#1F1F26' : '#0A0A0E';
          return (
            <div
              key={i}
              className="aspect-square flex items-center justify-center"
              style={{
                backgroundColor: bg,
                color: isPiece ? '#0A0A0E' : `${piece.accent}A0`,
                fontSize: '0.65rem',
                fontWeight: 900,
              }}
            >
              {isPiece ? piece.glyph : (isAttacked || isPawnMove) ? '•' : isPawnCap ? '×' : ''}
            </div>
          );
        })}
      </div>
      {piece.id === 'pawn' && (
        <div className="mt-1.5 flex items-center gap-2 text-[8px] font-black tracking-widest">
          <span style={{ color: `${piece.accent}` }}>● MOVE</span>
          <span className="text-red-400">× CAPTURE</span>
        </div>
      )}
    </div>
  );
};

// ---------- STAT BAR ----------
const StatBar = ({ stat, pieceId }) => (
  <div className="flex items-center gap-2" data-testid={`${pieceId}-stat-${stat.key.toLowerCase()}`}>
    <span className="text-[9px] font-black tracking-widest text-zinc-500 w-8">{stat.key}</span>
    <div className="flex-1 h-1.5 bg-zinc-900 overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${stat.value}%`, backgroundColor: stat.color }} />
    </div>
    <span className="text-[10px] font-black tabular-nums text-white w-6 text-right">{stat.value}</span>
  </div>
);

// ---------- ROSTER CARD ----------
const RosterCard = ({ piece }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <button
      type="button"
      onClick={() => setFlipped((f) => !f)}
      className="relative w-full text-left group focus:outline-none"
      data-testid={`roster-card-${piece.id}`}
      aria-label={`Tap to flip ${piece.name} coach card`}
    >
      {/* FRONT */}
      {!flipped && (
        <div
          className="relative overflow-hidden border-2 transition-shadow"
          style={{
            borderColor: piece.accent,
            background: 'linear-gradient(135deg, #12121A 0%, #1B1B24 40%, #0A0A0E 100%)',
            boxShadow: `0 0 0 0 ${piece.accent}00`,
          }}
          data-testid={`${piece.id}-card-front`}
        >
          <div className="absolute top-0 right-0 w-20 h-20 pointer-events-none"
               style={{ background: `linear-gradient(225deg, ${piece.accent}55 0%, transparent 60%)` }} />

          {/* Header */}
          <div className="flex items-start justify-between p-3 pb-1.5">
            <div>
              <div className="text-[8px] font-black tracking-[0.25em]" style={{ color: piece.accent }}>{piece.position}</div>
              <div className="text-3xl font-black text-white leading-none mt-0.5">{piece.code}</div>
              <div className="text-[10px] font-bold text-zinc-400 mt-0.5">{piece.name} · {piece.role}</div>
            </div>
            <div className="text-right">
              <div className="text-[8px] font-black tracking-widest text-zinc-500">OVR</div>
              <div className="text-3xl font-black leading-none" style={{ color: piece.accent }}>{piece.ovr}</div>
            </div>
          </div>

          {/* Glyph strip */}
          <div className="relative h-20 border-y-2 overflow-hidden flex items-center justify-center"
               style={{
                 borderColor: `${piece.accent}40`,
                 background: `radial-gradient(ellipse at center, ${piece.accent}22 0%, transparent 70%)`,
               }}>
            <div className="text-[5rem] leading-none select-none" style={{ color: piece.accent, textShadow: `0 0 25px ${piece.accent}88` }}>
              {piece.glyph}
            </div>
            <div className="absolute bottom-0.5 left-2 text-[7px] font-black tracking-[0.25em] text-zinc-600">
              VALUE · {piece.value}
            </div>
            <div className="absolute bottom-0.5 right-2 text-[7px] font-black tracking-[0.25em] text-zinc-600">TAP TO FLIP →</div>
          </div>

          {/* Stats */}
          <div className="p-3 space-y-1">
            {piece.stats.map((s) => <StatBar key={s.key} stat={s} pieceId={piece.id} />)}
          </div>

          {/* Comps */}
          <div className="px-3 pb-3 pt-1 border-t border-zinc-900">
            <div className="text-[8px] font-black tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
              <Users className="w-2.5 h-2.5" /> PLAYS LIKE
            </div>
            <div className="flex flex-wrap gap-1">
              {piece.comps.map((c) => (
                <span key={c} className="text-[9px] font-bold text-zinc-300 border border-zinc-800 px-1.5 py-0.5">{c}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* BACK */}
      {flipped && (
        <div
          className="relative overflow-hidden border-2"
          style={{
            borderColor: piece.accent,
            background: 'linear-gradient(135deg, #0A0A0E 0%, #14141C 50%, #0A0A0E 100%)',
          }}
          data-testid={`${piece.id}-card-back`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 pb-1.5">
            <div>
              <div className="text-[9px] font-black tracking-[0.25em]" style={{ color: piece.accent }}>MOVEMENT · CAPABILITIES</div>
              <div className="text-lg font-black text-white leading-none mt-0.5">{piece.code} · {piece.name}</div>
            </div>
            <div className="text-[8px] font-black tracking-widest text-zinc-500 flex items-center gap-1">
              <RotateCcw className="w-2.5 h-2.5" /> FLIP BACK
            </div>
          </div>

          {/* Diagram + Pattern */}
          <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-start">
            <MoveDiagram piece={piece} />
            <div className="space-y-2 min-w-0">
              <div>
                <div className="text-[9px] font-black tracking-widest mb-0.5 flex items-center gap-1" style={{ color: piece.accent }}>
                  <Move className="w-2.5 h-2.5" /> HOW SHE MOVES
                </div>
                <p className="text-[10px] text-zinc-300 leading-relaxed">{piece.movePattern}</p>
              </div>
              <div>
                <div className="text-[9px] font-black tracking-widest mb-0.5 flex items-center gap-1" style={{ color: piece.accent }}>
                  <Sparkles className="w-2.5 h-2.5" /> CAPABILITIES
                </div>
                <ul className="space-y-0.5">
                  {piece.capabilities.map((c, i) => (
                    <li key={i} className="text-[10px] text-zinc-300 leading-snug flex gap-1.5">
                      <span style={{ color: piece.accent }}>▸</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Coach's Note */}
          <div className="mx-3 mb-3 p-2 border" style={{ borderColor: `${piece.accent}55`, background: `${piece.accent}0A` }}>
            <div className="text-[9px] font-black tracking-widest mb-0.5 flex items-center gap-1" style={{ color: piece.accent }}>
              <ShieldAlert className="w-2.5 h-2.5" /> COACH&apos;S NOTE
            </div>
            <p className="text-[10px] text-zinc-300 leading-relaxed">{piece.coachNote}</p>
          </div>

          {/* Signature Moves */}
          <div className="border-t border-zinc-900 p-3">
            <div className="text-[9px] font-black tracking-widest mb-1.5" style={{ color: piece.accent }}>SIGNATURE MOVES</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {piece.signatureMoves.map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.name} className="border border-zinc-800 p-1.5 bg-black/40" data-testid={`${piece.id}-sig-${m.name.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="flex items-center gap-1 text-[10px] font-black text-white">
                      <Icon className="w-2.5 h-2.5" style={{ color: piece.accent }} /> {m.name}
                    </div>
                    <p className="text-[9px] text-zinc-400 leading-snug mt-0.5">{m.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

// ---------- ROSTER ----------
const ChessRoster = () => (
  <div className="my-6" data-testid="chess-roster-wrapper">
    <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#C9A961] mb-1 flex items-center gap-2">
      <Crown className="w-3.5 h-3.5" /> Roster Reveal · The Full Starting Six
    </div>
    <div className="text-[11px] text-zinc-400 mb-3">
      Tap any card to see how they move + their signature plays.
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {PIECES.map((p) => <RosterCard key={p.id} piece={p} />)}
    </div>
  </div>
);

export default ChessRoster;
